// src/components/SingularProvider.tsx
import { useCallback, useEffect, useMemo, useState } from "react";

// src/context/SingularContext.ts
import { createContext, useContext } from "react";
var SingularContext = /* @__PURE__ */ createContext(null);
var useSingularContext = () => {
  const context = useContext(SingularContext);
  if (!context) {
    return {
      sdk: null,
      config: null,
      onError: void 0
    };
  }
  return context;
};

// src/types/singular.ts
var SingularErrorCode = /* @__PURE__ */ function(SingularErrorCode2) {
  SingularErrorCode2["MISSING_SDK_KEY"] = "MISSING_SDK_KEY";
  SingularErrorCode2["MISSING_SDK_SECRET"] = "MISSING_SDK_SECRET";
  SingularErrorCode2["MISSING_PRODUCT_ID"] = "MISSING_PRODUCT_ID";
  SingularErrorCode2["INVALID_SDK_KEY"] = "INVALID_SDK_KEY";
  SingularErrorCode2["INVALID_SDK_SECRET"] = "INVALID_SDK_SECRET";
  SingularErrorCode2["SDK_NOT_INITIALIZED"] = "SDK_NOT_INITIALIZED";
  SingularErrorCode2["INITIALIZATION_FAILED"] = "INITIALIZATION_FAILED";
  SingularErrorCode2["CONFIG_VALIDATION_FAILED"] = "CONFIG_VALIDATION_FAILED";
  SingularErrorCode2["SESSION_CLEANUP_FAILED"] = "SESSION_CLEANUP_FAILED";
  SingularErrorCode2["TRACKING_FAILED"] = "TRACKING_FAILED";
  SingularErrorCode2["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
  return SingularErrorCode2;
}({});
var SingularError = class extends Error {
  constructor(code, message, originalError) {
    super(message);
    this.name = "SingularError";
    this.code = code;
    this.originalError = originalError;
  }
};

// src/utils/sdk.ts
var createSingularSDK = () => {
  let singularInstance = null;
  let isInitialized = false;
  return {
    async init(config) {
      try {
        const {
          singularSdk,
          SingularConfig: SingularSDKConfig
        } = await import("singular-sdk");
        const sdkConfig = new SingularSDKConfig(config.sdkKey, config.sdkSecret, config.productId);
        await singularSdk.init(sdkConfig);
        singularInstance = singularSdk;
        isInitialized = true;
      } catch (error) {
        throw new SingularError(SingularErrorCode.INITIALIZATION_FAILED, `Singular SDK initialization failed: ${error instanceof Error ? error.message : "Unknown error"}`, error instanceof Error ? error : void 0);
      }
    },
    event(params) {
      if (!isInitialized || !singularInstance) {
        throw new SingularError(SingularErrorCode.SDK_NOT_INITIALIZED, "Singular SDK is not initialized");
      }
      singularInstance.event(params.eventName, params.attributes);
    },
    revenue(params) {
      if (!isInitialized || !singularInstance) {
        throw new SingularError(SingularErrorCode.SDK_NOT_INITIALIZED, "Singular SDK is not initialized");
      }
      singularInstance.revenue(params.eventName, params.currency, params.amount, params.attributes);
    }
  };
};

// src/components/provider-utils.ts
var validateSingularConfig = (config) => {
  if (!config.sdkKey || config.sdkKey.trim() === "") {
    throw new SingularError(SingularErrorCode.MISSING_SDK_KEY, "Singular SDK key is required but not provided or empty");
  }
  if (!config.sdkSecret || config.sdkSecret.trim() === "") {
    throw new SingularError(SingularErrorCode.MISSING_SDK_SECRET, "Singular SDK secret is required but not provided or empty");
  }
  if (!config.productId || config.productId.trim() === "") {
    throw new SingularError(SingularErrorCode.MISSING_PRODUCT_ID, "Singular productId is required but not provided or empty");
  }
};
var wrapError = (err, code, message) => {
  if (err instanceof SingularError) {
    return err;
  }
  const originalError = err instanceof Error ? err : void 0;
  return new SingularError(code, message, originalError);
};
var createAndInitializeSdk = async (config) => {
  validateSingularConfig(config);
  let sdk;
  try {
    sdk = createSingularSDK();
  } catch (sdkCreationError) {
    throw new SingularError(SingularErrorCode.INITIALIZATION_FAILED, "Failed to create Singular SDK instance", sdkCreationError instanceof Error ? sdkCreationError : void 0);
  }
  await sdk.init(config);
  return sdk;
};

// src/components/SingularProvider.tsx
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
var SingularProvider = ({
  config,
  children,
  onError,
  autoInit = false
}) => {
  const [sdk, setSdk] = useState(null);
  const initializeSdk = useCallback(async (newConfig) => {
    try {
      const newSdk = await createAndInitializeSdk(newConfig);
      setSdk(newSdk);
    } catch (err) {
      setSdk(null);
      const error = wrapError(err, SingularErrorCode.INITIALIZATION_FAILED, "Singular SDK initialization failed");
      onError?.(error);
    }
  }, [onError]);
  useEffect(() => {
    if (!autoInit)
      return;
    initializeSdk(config);
  }, [autoInit, config, initializeSdk]);
  const contextValue = useMemo(() => ({
    sdk,
    config,
    onError
  }), [sdk, config, onError]);
  try {
    return /* @__PURE__ */ _jsx(SingularContext.Provider, {
      value: contextValue,
      children
    });
  } catch (renderError) {
    const error = new SingularError(SingularErrorCode.UNKNOWN_ERROR, "Unexpected error during SingularProvider render", renderError instanceof Error ? renderError : void 0);
    onError?.(error);
    return /* @__PURE__ */ _jsx(_Fragment, {
      children
    });
  }
};

// src/hooks/useSingular.ts
import { useCallback as useCallback2 } from "react";
var convertPropertiesForSingular = (properties) => {
  if (!properties) {
    return void 0;
  }
  const converted = {};
  if (properties && typeof properties === "object" && !Array.isArray(properties)) {
    for (const [key, value] of Object.entries(properties)) {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        converted[key] = value;
      } else if (value !== null && value !== void 0) {
        if (typeof value === "object") {
          converted[key] = JSON.stringify(value);
        } else {
          converted[key] = String(value);
        }
      }
    }
  }
  return converted;
};
var getEventName = (eventName, config) => {
  return config?.isProduction ? eventName : `${eventName}_test`;
};
var useSingular = () => {
  const context = useSingularContext();
  const {
    sdk,
    config,
    onError
  } = context;
  const track = useCallback2((eventName, properties) => {
    if (!sdk || !config) {
      return;
    }
    try {
      const finalEventName = getEventName(eventName, config);
      const convertedProperties = convertPropertiesForSingular(properties);
      const shouldTrackRevenue = config?.revenueEventName && convertedProperties && config?.isProduction && config?.isRevenueEvent?.(eventName, convertedProperties);
      if (shouldTrackRevenue) {
        const amount = typeof convertedProperties.amount === "number" ? convertedProperties.amount : 0;
        const currency = typeof convertedProperties.currency === "string" ? convertedProperties.currency : config?.defaultCurrency;
        if (currency && config?.revenueEventName && amount > 0) {
          sdk.revenue({
            eventName: config.revenueEventName,
            attributes: convertedProperties,
            amount,
            currency
          });
        }
      }
      sdk.event({
        eventName: finalEventName,
        attributes: convertedProperties
      });
    } catch (trackingError) {
      onError?.(new SingularError(SingularErrorCode.TRACKING_FAILED, `Failed to track event "${eventName}": ${trackingError instanceof Error ? trackingError.message : "Unknown error"}`, trackingError instanceof Error ? trackingError : void 0));
    }
  }, [sdk, config, onError]);
  return {
    isInitialized: !!sdk,
    track
  };
};

// src/utils/factory.ts
import { getIsMobileAppMode } from "@taxfix/scouts-helpers";
import * as Sentry from "@sentry/react";
var createSingularSetup = (config) => {
  const shouldInitializeSingularWeb = () => {
    return !getIsMobileAppMode();
  };
  const handleSingularError = (error) => {
    Sentry.withScope((scope) => {
      scope.setTag("component", "Singular");
      scope.setContext("singular_error", {
        message: error.message,
        stack: error.stack
      });
      Sentry.captureException(error);
    });
  };
  return {
    config,
    shouldInitializeSingularWeb,
    handleSingularError
  };
};

// src/index.ts
var Singular = {
  Provider: SingularProvider,
  useSingular,
  useSingularContext
};
export {
  Singular,
  SingularError,
  SingularErrorCode,
  SingularProvider,
  createSingularSDK,
  createSingularSetup,
  useSingular,
  useSingularContext
};
//# sourceMappingURL=index.js.map